import {
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router";
import { api } from "../api/client";
import { qk, useProject, useTasks, useWorkflow } from "../api/queries";
import type { Stage, Task } from "../api/types";
import { BoardColumn, COLUMN_PREFIX } from "../components/board/BoardColumn";
import { CreateTaskModal } from "../components/board/CreateTaskModal";
import { NewTaskInline } from "../components/board/NewTaskInline";
import { TaskCardBody } from "../components/board/TaskCard";
import { useDialogs } from "../components/Dialogs";
import { Icon } from "../components/Icon";
import { ErrorBox, PageLoading } from "../components/Spinner";

export function BoardPage() {
  const { pid = "", tid } = useParams();
  const navigate = useNavigate();
  const project = useProject(pid);
  const workflow = useWorkflow(pid);
  const tasksQ = useTasks(pid);
  const [showCreate, setShowCreate] = useState(false);

  const openTask = (t: { id: string }) => navigate(`/p/${pid}/tasks/${t.id}`);

  if (project.error) return <ErrorBox error={project.error} />;
  if (workflow.error) return <ErrorBox error={workflow.error} />;
  if (tasksQ.error) return <ErrorBox error={tasksQ.error} />;
  if (!project.data || !workflow.data || !tasksQ.data) return <PageLoading />;

  const p = project.data;
  return (
    <div className="board-page">
      <header className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">{p.name}</h1>
          <div className="page-subtitle muted">
            {p.github_repo ?? p.repo_url} · <code>{p.base_branch}</code> · workflow v{workflow.data.version}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Icon name="plus" size={14} /> New task
        </button>
      </header>
      <Board
        pid={pid}
        stages={workflow.data.stages}
        tasks={tasksQ.data}
        selectedId={tid}
        onOpen={openTask}
        onOpenForm={() => setShowCreate(true)}
      />
      {showCreate && (
        <CreateTaskModal
          pid={pid}
          onClose={() => setShowCreate(false)}
          onCreated={(t) => {
            setShowCreate(false);
            openTask(t);
          }}
        />
      )}
      <Outlet />
    </div>
  );
}

function Board({
  pid,
  stages,
  tasks,
  selectedId,
  onOpen,
  onOpenForm,
}: {
  pid: string;
  stages: Stage[];
  tasks: Task[];
  selectedId: string | undefined;
  onOpen: (t: { id: string }) => void;
  onOpenForm: () => void;
}) {
  const qc = useQueryClient();
  const { confirm } = useDialogs();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<string | null>(null);

  const { columns, byId, stageByKey } = useMemo(() => {
    const stageByKey = new Map(stages.map((s) => [s.key, s]));
    const keys = stages.map((s) => s.key);
    // Tasks pinned to older workflow versions may sit in stages that no longer exist.
    for (const t of tasks) if (!stageByKey.has(t.stage_key) && !keys.includes(t.stage_key)) keys.push(t.stage_key);
    const columns = new Map<string, Task[]>(keys.map((k) => [k, []]));
    for (const t of tasks) columns.get(t.stage_key)!.push(t);
    for (const list of columns.values()) list.sort((a, b) => a.position - b.position || a.number - b.number);
    return { columns, byId: new Map(tasks.map((t) => [t.id, t])), stageByKey };
  }, [stages, tasks]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const columnOf = (id: string | number | undefined): string | null => {
    if (id === undefined) return null;
    const s = String(id);
    if (s.startsWith(COLUMN_PREFIX)) return s.slice(COLUMN_PREFIX.length);
    return byId.get(s)?.stage_key ?? null;
  };

  const setTasks = (fn: (ts: Task[]) => Task[]) =>
    qc.setQueryData<Task[]>(qk.tasks(pid), (old) => (old ? fn(old) : old));

  const reorder = useMutation({
    mutationFn: (v: { id: string; before: string | null; after: string | null; position: number }) =>
      api.reorderTask(v.id, v.before, v.after),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: qk.tasks(pid) });
      setTasks((ts) => ts.map((t) => (t.id === v.id ? { ...t, position: v.position } : t)));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.tasks(pid) }),
  });

  const move = useMutation({
    mutationFn: (v: { id: string; stage: string }) => api.moveTask(v.id, v.stage),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: qk.tasks(pid) });
      setTasks((ts) => ts.map((t) => (t.id === v.id ? { ...t, stage_key: v.stage } : t)));
    },
    onSettled: (_d, _e, v) => {
      qc.invalidateQueries({ queryKey: qk.tasks(pid) });
      qc.invalidateQueries({ queryKey: qk.task(v.id) });
    },
  });

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const onDragOver = (e: DragOverEvent) => setOverCol(columnOf(e.over?.id));
  const onDragCancel = () => {
    setActiveId(null);
    setOverCol(null);
  };

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    setOverCol(null);
    const task = byId.get(String(active.id));
    const to = columnOf(over?.id);
    if (!task || !over || !to) return;
    const from = task.stage_key;

    if (from === to) {
      const ids = (columns.get(from) ?? []).map((t) => t.id);
      const oldIndex = ids.indexOf(task.id);
      const overIsColumn = String(over.id).startsWith(COLUMN_PREFIX);
      const newIndex = overIsColumn ? ids.length - 1 : ids.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      const next = arrayMove(ids, oldIndex, newIndex);
      const i = next.indexOf(task.id);
      // before_task_id = the card that will sit directly above; after_task_id = directly below.
      const before = next[i - 1] ?? null;
      const after = next[i + 1] ?? null;
      const bp = before ? byId.get(before)!.position : null;
      const ap = after ? byId.get(after)!.position : null;
      const position = bp !== null && ap !== null ? (bp + ap) / 2 : bp !== null ? bp + 1 : ap !== null ? ap - 1 : 0;
      reorder.mutate({ id: task.id, before, after, position });
      return;
    }

    const toStage = stageByKey.get(to);
    if (!toStage) return;
    const fromName = stageByKey.get(from)?.name ?? from;
    const ok = await confirm({
      title: `Move #${task.number} to ${toStage.name}?`,
      body: (
        <p>
          This manually moves the task from <strong>{fromName}</strong> to <strong>{toStage.name}</strong>
          {toStage.kind === "agent" ? ", where an agent may pick it up" : ""}. The move is recorded in the timeline.
        </p>
      ),
      confirmLabel: "Move task",
    });
    if (ok) move.mutate({ id: task.id, stage: to });
  };

  const active = activeId ? byId.get(activeId) : undefined;
  const firstKey = stages[0]?.key;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div className="board" role="list">
        {[...columns.entries()].map(([key, list]) => (
          <BoardColumn
            key={key}
            stageKey={key}
            stage={stageByKey.get(key)}
            tasks={list}
            selectedId={selectedId}
            onOpen={onOpen}
            highlighted={!!active && overCol === key && active.stage_key !== key}
            header={
              key === firstKey ? (
                <NewTaskInline pid={pid} onOpenForm={onOpenForm} onCreated={onOpen} />
              ) : undefined
            }
          />
        ))}
      </div>
      <DragOverlay dropAnimation={null}>
        {active ? (
          <div className="card card-overlay">
            <TaskCardBody task={active} stage={stageByKey.get(active.stage_key)} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
